/**
 * Notification Type Keys and Validation Constants
 * 
 * This file is the single source of truth for notification type definitions.
 * All type keys must be validated against these constants before database insertion.
 * 
 * @see docs/notifications-architecture-proposal.md for full architecture details
 */

// ============================================================================
// NOTIFICATION TYPE KEYS
// ============================================================================

/**
 * Stable notification type keys following the pattern: {category}.{event}
 * These keys are used in the `type_key` column of the notifications table.
 */
export const NOTIFICATION_TYPES = {
  // Attendance notifications
  ATTENDANCE_MISSED_CHECKIN: 'attendance.missed_checkin',
  ATTENDANCE_MISSED_CHECKOUT: 'attendance.missed_checkout',
  ATTENDANCE_LATE_ARRIVAL: 'attendance.late_arrival',
  ATTENDANCE_EARLY_CHECKOUT: 'attendance.early_checkout',
  ATTENDANCE_UNSCHEDULED_CHECKIN: 'attendance.unscheduled_checkin',
  ATTENDANCE_OUTSIDE_GEOFENCE: 'attendance.outside_geofence',
  
  // Timesheet notifications
  TIMESHEET_APPROVED: 'timesheet.approved',
  TIMESHEET_REJECTED: 'timesheet.rejected',
  
  // Shift notifications
  SHIFT_SWAP_REQUESTED: 'shift.swap_requested',
  SHIFT_SWAP_STATUS_CHANGED: 'shift.swap_status_changed',
  
  // Leave notifications
  LEAVE_REQUEST_SUBMITTED: 'leave.request_submitted',
  LEAVE_REQUEST_STATUS_CHANGED: 'leave.request_status_changed',
} as const;

/**
 * Type for notification type key values
 */
export type NotificationTypeKey = typeof NOTIFICATION_TYPES[keyof typeof NOTIFICATION_TYPES];

/**
 * Array of all valid notification type keys for validation
 */
export const VALID_NOTIFICATION_TYPE_KEYS: NotificationTypeKey[] = Object.values(NOTIFICATION_TYPES);

/**
 * Validate if a type key is valid
 */
export function isValidNotificationTypeKey(typeKey: string): typeKey is NotificationTypeKey {
  return VALID_NOTIFICATION_TYPE_KEYS.includes(typeKey as NotificationTypeKey);
}

// ============================================================================
// NOTIFICATION CATEGORIES
// ============================================================================

/**
 * Notification categories derived from type keys (the part before the dot)
 */
export const NOTIFICATION_CATEGORIES = {
  ATTENDANCE: 'attendance',
  TIMESHEET: 'timesheet',
  SHIFT: 'shift',
  LEAVE: 'leave',
} as const;

export type NotificationCategory = typeof NOTIFICATION_CATEGORIES[keyof typeof NOTIFICATION_CATEGORIES];

/**
 * Get the category from a type key
 */
export function getNotificationCategory(typeKey: NotificationTypeKey): NotificationCategory {
  const category = typeKey.split('.')[0] as NotificationCategory;
  return category;
}

// ============================================================================
// SEVERITY LEVELS
// ============================================================================

/**
 * Notification severity levels matching the database schema
 * Consistent with attendance_event_flags severity patterns
 */
export const NOTIFICATION_SEVERITY = {
  INFO: 'info',
  WARNING: 'warning',
  ERROR: 'error',
} as const;

export type NotificationSeverity = typeof NOTIFICATION_SEVERITY[keyof typeof NOTIFICATION_SEVERITY];

export const VALID_SEVERITY_LEVELS: NotificationSeverity[] = Object.values(NOTIFICATION_SEVERITY);

export function isValidSeverity(severity: string): severity is NotificationSeverity {
  return VALID_SEVERITY_LEVELS.includes(severity as NotificationSeverity);
}

// ============================================================================
// DELIVERY CHANNELS
// ============================================================================

/**
 * Supported notification delivery channels
 */
export const NOTIFICATION_CHANNELS = {
  IN_APP: 'in_app',
  EMAIL: 'email',
  // PUSH: 'push', // Future: not implemented yet
} as const;

export type NotificationChannel = typeof NOTIFICATION_CHANNELS[keyof typeof NOTIFICATION_CHANNELS];

export const VALID_CHANNELS: NotificationChannel[] = Object.values(NOTIFICATION_CHANNELS);

export function isValidChannel(channel: string): channel is NotificationChannel {
  return VALID_CHANNELS.includes(channel as NotificationChannel);
}

/**
 * Default channels for new preferences
 */
export const DEFAULT_CHANNELS: NotificationChannel[] = [NOTIFICATION_CHANNELS.IN_APP];

// ============================================================================
// DELIVERY STATUS
// ============================================================================

/**
 * Notification delivery status values
 */
export const DELIVERY_STATUS = {
  PENDING: 'pending',
  DELIVERED: 'delivered',
  FAILED: 'failed',
} as const;

export type DeliveryStatus = typeof DELIVERY_STATUS[keyof typeof DELIVERY_STATUS];

// ============================================================================
// QUEUE STATUS
// ============================================================================

/**
 * Delivery queue processing status values
 */
export const QUEUE_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  DELIVERED: 'delivered',
  FAILED: 'failed',
} as const;

export type QueueStatus = typeof QUEUE_STATUS[keyof typeof QUEUE_STATUS];

// ============================================================================
// VALIDATION CONSTANTS
// ============================================================================

/**
 * Maximum payload size in bytes (10KB as per architecture spec)
 */
export const MAX_PAYLOAD_SIZE_BYTES = 10 * 1024;

/**
 * Maximum number of notifications per minute per actor (rate limiting)
 */
export const MAX_NOTIFICATIONS_PER_MINUTE = 100;

/**
 * Retention periods for notifications (in days)
 */
export const RETENTION_PERIODS = {
  /** Default retention for all notifications */
  DEFAULT_DAYS: 90,
  /** Transient alerts like shift reminders */
  TRANSIENT_DAYS: 7,
  /** Action-required notifications like approvals */
  ACTION_REQUIRED_DAYS: 30,
  /** Grace period after expiration before deletion */
  POST_EXPIRATION_GRACE_DAYS: 30,
} as const;

/**
 * Retry configuration for delivery queue
 */
export const RETRY_CONFIG = {
  /** Maximum number of delivery attempts */
  MAX_ATTEMPTS: 3,
  /** Retry delays in minutes: 1min, 5min, 30min */
  RETRY_DELAYS_MINUTES: [1, 5, 30] as const,
  /** Timeout threshold for stale processing detection (minutes) */
  STALE_PROCESSING_TIMEOUT_MINUTES: 5,
} as const;

// ============================================================================
// TYPE KEY TO METADATA MAPPING
// ============================================================================

/**
 * Metadata for each notification type including default severity and channels
 */
export const NOTIFICATION_TYPE_METADATA: Record<NotificationTypeKey, {
  category: NotificationCategory;
  defaultSeverity: NotificationSeverity;
  defaultChannels: NotificationChannel[];
  expirationDays: number | null; // null = never expires (uses default 90-day cleanup)
  description: string;
  selfLabel?: string; // Optional label for self-notification contexts (e.g., "My Notifications")
  hiddenFromPersonalSettings?: boolean; // Hide from personal settings (admin-only via Communications)
}> = {
  [NOTIFICATION_TYPES.ATTENDANCE_MISSED_CHECKIN]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.WARNING,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.ACTION_REQUIRED_DAYS,
    description: 'Employee missed scheduled check-in',
    selfLabel: 'Missed scheduled check-in',
  },
  [NOTIFICATION_TYPES.ATTENDANCE_MISSED_CHECKOUT]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.WARNING,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.ACTION_REQUIRED_DAYS,
    description: 'Employee missed scheduled check-out',
    selfLabel: 'Missed scheduled check-out',
  },
  [NOTIFICATION_TYPES.ATTENDANCE_LATE_ARRIVAL]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Employee arrived late for scheduled shift',
    selfLabel: 'Arrived late for scheduled shift',
  },
  [NOTIFICATION_TYPES.ATTENDANCE_EARLY_CHECKOUT]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.WARNING,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Employee left early before scheduled shift end',
    selfLabel: 'Left early before scheduled shift end',
  },
  [NOTIFICATION_TYPES.ATTENDANCE_UNSCHEDULED_CHECKIN]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Employee checked in without a scheduled shift',
    selfLabel: 'Checked in without a scheduled shift',
  },
  [NOTIFICATION_TYPES.ATTENDANCE_OUTSIDE_GEOFENCE]: {
    category: NOTIFICATION_CATEGORIES.ATTENDANCE,
    defaultSeverity: NOTIFICATION_SEVERITY.WARNING,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Employee clocked in/out from outside geofence',
    selfLabel: 'Clocked in/out from outside allowed area',
  },
  [NOTIFICATION_TYPES.TIMESHEET_APPROVED]: {
    category: NOTIFICATION_CATEGORIES.TIMESHEET,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Timesheet has been approved',
  },
  [NOTIFICATION_TYPES.TIMESHEET_REJECTED]: {
    category: NOTIFICATION_CATEGORIES.TIMESHEET,
    defaultSeverity: NOTIFICATION_SEVERITY.WARNING,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.ACTION_REQUIRED_DAYS,
    description: 'Timesheet has been rejected and requires attention',
  },
  [NOTIFICATION_TYPES.SHIFT_SWAP_REQUESTED]: {
    category: NOTIFICATION_CATEGORIES.SHIFT,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.ACTION_REQUIRED_DAYS,
    description: 'A shift swap has been requested',
  },
  [NOTIFICATION_TYPES.SHIFT_SWAP_STATUS_CHANGED]: {
    category: NOTIFICATION_CATEGORIES.SHIFT,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Shift swap request status has changed',
  },
  [NOTIFICATION_TYPES.LEAVE_REQUEST_SUBMITTED]: {
    category: NOTIFICATION_CATEGORIES.LEAVE,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.ACTION_REQUIRED_DAYS,
    description: 'A leave request has been submitted for approval',
    hiddenFromPersonalSettings: true,
  },
  [NOTIFICATION_TYPES.LEAVE_REQUEST_STATUS_CHANGED]: {
    category: NOTIFICATION_CATEGORIES.LEAVE,
    defaultSeverity: NOTIFICATION_SEVERITY.INFO,
    defaultChannels: [NOTIFICATION_CHANNELS.IN_APP],
    expirationDays: RETENTION_PERIODS.TRANSIENT_DAYS,
    description: 'Leave request status has changed',
  },
};

// ============================================================================
// WILDCARD SUPPORT
// ============================================================================

/**
 * Wildcard suffix for category-level preferences
 * e.g., 'attendance.*' matches all attendance notifications
 */
export const WILDCARD_SUFFIX = '.*';

/**
 * Check if a type key matches a preference key (supports wildcards)
 * Examples:
 *   matchesTypeKey('attendance.missed_checkin', 'attendance.missed_checkin') => true
 *   matchesTypeKey('attendance.missed_checkin', 'attendance.*') => true
 *   matchesTypeKey('attendance.missed_checkin', 'timesheet.*') => false
 */
export function matchesTypeKey(typeKey: NotificationTypeKey, preferenceKey: string): boolean {
  // Exact match
  if (typeKey === preferenceKey) {
    return true;
  }
  
  // Wildcard match
  if (preferenceKey.endsWith(WILDCARD_SUFFIX)) {
    const category = preferenceKey.slice(0, -WILDCARD_SUFFIX.length);
    return typeKey.startsWith(category + '.');
  }
  
  return false;
}

// ============================================================================
// RECIPIENT CATEGORIES (role-based targeting)
// ============================================================================

/**
 * Role slugs for notification targeting
 * Must match lowercase role slugs from the database
 */
export const RECIPIENT_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  SUPERVISOR: 'supervisor',
  EMPLOYEE: 'employee',
} as const;

export type RecipientRole = typeof RECIPIENT_ROLES[keyof typeof RECIPIENT_ROLES];
